<template>
  <component :is="embedded ? 'div' : 'v-card'" flat outlined :class="rootClass">
    <div class="mto-doorphone-status-chart__title">{{ title }}</div>
    <div v-if="hasStatusChartData" class="mto-doorphone-status-chart__canvas-wrap">
      <canvas ref="statusChart"></canvas>
    </div>
    <div v-else class="mto-doorphone-status-chart__empty">
      {{ emptyText }}
    </div>
  </component>
</template>

<script>
import Chart from 'chart.js';

export const STATUS_COLORS = {
  st_2: 'rgba(76, 175, 80, 0.85)',
  st_null: 'rgba(255, 152, 0, 0.85)',
  'st_-1': 'rgba(120, 144, 156, 0.85)',
  st_5: 'rgba(33, 150, 243, 0.85)',
  st_6: 'rgba(156, 39, 176, 0.85)',
  st_7: 'rgba(0, 188, 212, 0.85)',
  st_8: 'rgba(121, 85, 72, 0.85)',
  st_9: 'rgba(244, 67, 54, 0.85)',
};

export function buildDoorphoneStatusChartData ({
  doorphonesIndicators = {},
  equipmentItems = [],
  statusDescription = {},
}) {
  const indicators = doorphonesIndicators || {};
  const fallbackCounts = {};
  equipmentItems
    .filter(item => item.type === 'doorphone')
    .forEach((item) => {
      const key = item.status_key || 'unknown';
      fallbackCounts[key] = Number(fallbackCounts[key] || 0) + 1;
    });

  const labels = [];
  const data = [];
  const backgroundColor = [];
  let statusesTotal = 0;

  Object.keys(STATUS_COLORS).forEach((key) => {
    const value = Number(indicators[key] || 0);
    if (!value) return;

    statusesTotal += value;
    labels.push(statusDescription[key] || key);
    data.push(value);
    backgroundColor.push(STATUS_COLORS[key]);
  });

  const indicatorsCount = Number(indicators.count || 0);
  const unknownCount = indicatorsCount > statusesTotal
    ? indicatorsCount - statusesTotal
    : 0;

  if (unknownCount > 0) {
    labels.push('Не определён');
    data.push(unknownCount);
    backgroundColor.push('rgba(158, 158, 158, 0.85)');
  }

  const doorphonesTotalFromItems = equipmentItems.filter(item => item.type === 'doorphone').length;

  if (!labels.length && doorphonesTotalFromItems > 0) {
    Object.keys(STATUS_COLORS).forEach((key) => {
      const value = Number(fallbackCounts[key] || 0);
      if (!value) return;
      labels.push(statusDescription[key] || key);
      data.push(value);
      backgroundColor.push(STATUS_COLORS[key]);
    });

    const fallbackUnknownCount = Number(fallbackCounts.unknown || 0);
    if (fallbackUnknownCount > 0) {
      labels.push('Не определён');
      data.push(fallbackUnknownCount);
      backgroundColor.push('rgba(158, 158, 158, 0.85)');
    }
  }

  if (!labels.length && indicatorsCount > 0) {
    labels.push('Домофоны (без статусов)');
    data.push(indicatorsCount);
    backgroundColor.push('rgba(25, 118, 210, 0.85)');
  }

  return { labels, datasets: [{ data, backgroundColor, borderWidth: 0 }] };
}

export function mergeDoorphoneIndicators (indicatorsList) {
  const merged = {
    count: 0,
    st_null: 0,
    'st_-1': 0,
    st_2: 0,
    st_5: 0,
    st_6: 0,
    st_7: 0,
    st_8: 0,
    st_9: 0,
  };

  (indicatorsList || []).forEach((indicators) => {
    if (!indicators) return;
    Object.keys(merged).forEach((key) => {
      merged[key] += Number(indicators[key] || 0);
    });
  });

  return merged;
}

export default {
  name: 'MtoDoorphoneStatusChart',
  props: {
    doorphonesIndicators: {
      type: Object,
      default: () => ({}),
    },
    equipmentItems: {
      type: Array,
      default: () => [],
    },
    statusDescription: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: 'Статусы домофонов',
    },
    emptyText: {
      type: String,
      default: 'Нет домофонов',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    statusChart: null,
  }),
  computed: {
    rootClass () {
      return this.embedded
        ? 'mto-doorphone-status-chart mto-doorphone-status-chart--embedded'
        : 'mto-doorphone-status-chart pa-4';
    },
    hasStatusChartData () {
      return this.buildStatusChartData().labels.length > 0;
    },
  },
  mounted () {
    this.renderStatusChart();
  },
  beforeDestroy () {
    this.destroyStatusChart();
  },
  watch: {
    doorphonesIndicators: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderStatusChart());
      },
    },
    equipmentItems: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderStatusChart());
      },
    },
    statusDescription: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderStatusChart());
      },
    },
    hasStatusChartData (value) {
      if (value) this.$nextTick(() => this.renderStatusChart());
      else this.destroyStatusChart();
    },
  },
  methods: {
    buildStatusChartData () {
      return buildDoorphoneStatusChartData({
        doorphonesIndicators: this.doorphonesIndicators,
        equipmentItems: this.equipmentItems,
        statusDescription: this.statusDescription,
      });
    },
    renderStatusChart () {
      if (!this.$refs.statusChart) return;

      const chartData = this.buildStatusChartData();
      this.destroyStatusChart();

      if (!chartData.labels.length) return;

      this.statusChart = new Chart(this.$refs.statusChart, {
        type: 'doughnut',
        data: chartData,
        options: {
          maintainAspectRatio: false,
          responsive: true,
          legend: {
            position: 'bottom',
          },
          tooltips: {
            callbacks: {
              label (tooltipItem, data) {
                const label = data.labels[tooltipItem.index] || '';
                const value = data.datasets[0].data[tooltipItem.index] || 0;
                return `${label}: ${value}`;
              },
            },
          },
        },
      });
    },
    destroyStatusChart () {
      if (!this.statusChart) return;
      this.statusChart.destroy();
      this.statusChart = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.mto-doorphone-status-chart {
  border-radius: 14px;
}

.mto-doorphone-status-chart--embedded {
  height: 100%;
}

.mto-doorphone-status-chart__title {
  margin-bottom: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #37474f;
}

.mto-doorphone-status-chart__canvas-wrap {
  position: relative;
  height: 280px;
}

.mto-doorphone-status-chart__empty {
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #90a4ae;
  text-align: center;
  padding: 0 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
}
</style>
